/**
 * Users — organization user management.
 *
 *   - Top stat strip: total users, org admins, members
 *   - Filter chips: All / Org admins / Members
 *   - Rich table with avatar initials (colored), name + email, role,
 *     last-login relative time, and per-row edit / delete
 *   - Right-side slide-out panel for create / edit
 *
 * Authorization: platform_admin and org_admin can manage users. Every
 * other role sees an "Access denied" message.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";

import * as api from "../api.js";
import {
  PageHeader,
  Loading,
  ErrorBanner,
  StatCard,
  formatDate,
} from "../components.jsx";
import { useAuth } from "../AuthContext.jsx";

const EMPTY_FORM = {
  email: "",
  name: "",
  password: "",
  role: "org_user",
  is_org_admin: false,
};

// Stable color from a string so avatars look distinct per user
function colorFromString(s) {
  const palette = [
    "#1f5fb0",
    "#0f7a47",
    "#9a6700",
    "#6f42c1",
    "#b42318",
    "#0b6e6e",
    "#805b10",
    "#6b2d99",
  ];
  let hash = 0;
  for (let i = 0; i < (s || "").length; i++) {
    hash = (hash * 31 + s.charCodeAt(i)) % 1000000;
  }
  return palette[hash % palette.length];
}

function relativeTime(d) {
  if (!d) return "Never";
  const then = new Date(d).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min${mins === 1 ? "" : "s"} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  return new Date(d).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export default function Users() {
  const { user, currentOrg, isPlatformAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);

  const orgId = currentOrg?._id;

  const load = useCallback(async () => {
    if (!orgId && !isPlatformAdmin) {
      setUsers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await api.listAppUsers(orgId || undefined);
      setUsers(res.data || []);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [orgId, isPlatformAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  // Derived stats + filters
  const stats = useMemo(() => {
    const totals = {
      total: users.length,
      orgAdmins: 0,
      platformAdmins: 0,
      members: 0,
    };
    for (const u of users) {
      if (u.role === "platform_admin") totals.platformAdmins++;
      else if (u.is_org_admin) totals.orgAdmins++;
      else totals.members++;
    }
    return totals;
  }, [users]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (roleFilter === "admins" && !u.is_org_admin && u.role !== "platform_admin")
        return false;
      if (roleFilter === "members" && (u.is_org_admin || u.role === "platform_admin"))
        return false;
      if (roleFilter === "platform" && u.role !== "platform_admin") return false;
      if (q) {
        const hay = `${u.name || ""} ${u.email} ${u.role}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [users, roleFilter, search]);

  if (!user?.is_org_admin && !isPlatformAdmin) {
    return (
      <div className="page-body">
        <div className="empty-state">
          <h3>Access denied</h3>
          <p>You need organization-admin permissions to manage users.</p>
        </div>
      </div>
    );
  }

  const startCreate = () => {
    setForm(EMPTY_FORM);
    setEditing("new");
  };

  const startEdit = (u) => {
    setForm({
      email: u.email,
      name: u.name || "",
      password: "",
      role: u.role,
      is_org_admin: !!u.is_org_admin,
    });
    setEditing(u._id);
  };

  const closePanel = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      if (editing === "new") {
        await api.createAppUser({
          email: form.email,
          name: form.name,
          password: form.password,
          role: isPlatformAdmin ? form.role : "org_user",
          org_id: orgId,
          is_org_admin: form.is_org_admin,
        });
      } else {
        const patch = { name: form.name, is_org_admin: form.is_org_admin };
        if (form.password) patch.password = form.password;
        if (isPlatformAdmin) patch.role = form.role;
        await api.updateAppUser(editing, patch);
      }
      closePanel();
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (u) => {
    if (u._id === user._id) {
      alert("You cannot delete your own account.");
      return;
    }
    if (!window.confirm(`Delete user "${u.email}"?`)) return;
    setBusy(true);
    try {
      await api.deleteAppUser(u._id);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const roleFilterOptions = [
    { id: "all", label: "Everyone", n: users.length },
    {
      id: "admins",
      label: "Admins",
      n: stats.orgAdmins + stats.platformAdmins,
    },
    { id: "members", label: "Members", n: stats.members },
    ...(isPlatformAdmin
      ? [{ id: "platform", label: "Platform admins", n: stats.platformAdmins }]
      : []),
  ];

  return (
    <>
      <PageHeader
        title="Users"
        subtitle={
          currentOrg
            ? `Manage user accounts in ${currentOrg.name}. Invite teammates, promote org admins, reset passwords.`
            : "Select an organization to manage its users."
        }
        breadcrumbs={[
          { label: currentOrg?.name || "Organization" },
          { label: "Users" },
        ]}
        actions={
          <button className="btn primary" onClick={startCreate}>
            + New user
          </button>
        }
      />

      <div className="page-body">
        <ErrorBanner error={error} />

        <div className="stat-grid">
          <StatCard label="Total users" value={stats.total} sub="in this workspace" />
          <StatCard
            label="Organization admins"
            value={stats.orgAdmins + stats.platformAdmins}
            sub={
              isPlatformAdmin && stats.platformAdmins > 0
                ? `${stats.platformAdmins} platform admin${stats.platformAdmins === 1 ? "" : "s"}`
                : "Can manage users + settings"
            }
          />
          <StatCard
            label="Members"
            value={stats.members}
            sub="Read/write access, no admin"
          />
        </div>

        <div
          className="mb-16"
          style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}
        >
          <div className="btn-row" style={{ flexWrap: "wrap" }}>
            {roleFilterOptions.map((o) => (
              <button
                key={o.id}
                className={`btn sm ${roleFilter === o.id ? "primary" : ""}`}
                onClick={() => setRoleFilter(o.id)}
              >
                {o.label}
                <span className="muted" style={{ marginLeft: 4 }}>
                  ({o.n})
                </span>
              </button>
            ))}
          </div>
          <div className="spacer" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              padding: "8px 12px",
              fontSize: 13,
              border: "1px solid var(--color-border)",
              borderRadius: "var(--radius-sm)",
              minWidth: 260,
            }}
          />
          <span className="muted small">
            {filtered.length} of {users.length}
          </span>
        </div>

        {loading ? (
          <Loading />
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <h3>
              {users.length === 0
                ? "No users yet"
                : "No users match your filter"}
            </h3>
            <p>
              {users.length === 0
                ? "Click + New user to invite the first teammate."
                : "Try a different filter or search term."}
            </p>
          </div>
        ) : (
          <div className="card" style={{ padding: 0 }}>
            <table
              className="table agents-table"
              style={{ border: "none" }}
            >
              <thead>
                <tr>
                  <th style={{ width: "32%" }}>Member</th>
                  <th style={{ width: 140 }}>Role</th>
                  <th style={{ width: 130 }}>Org admin</th>
                  <th style={{ width: 180 }}>Last sign-in</th>
                  <th style={{ width: 180 }} className="right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => {
                  const isSelf = u._id === user._id;
                  const avatarColor = colorFromString(u.email);
                  const initials = (u.name || u.email)
                    .split(/\s+/)
                    .map((p) => p.charAt(0))
                    .join("")
                    .slice(0, 2)
                    .toUpperCase();
                  return (
                    <tr key={u._id}>
                      <td>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                          }}
                        >
                          <div
                            className="user-avatar"
                            style={{ background: avatarColor }}
                          >
                            {initials}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 600 }}>
                              {u.name || u.email}
                              {isSelf && (
                                <span
                                  className="badge badge-neutral"
                                  style={{ marginLeft: 8 }}
                                >
                                  you
                                </span>
                              )}
                            </div>
                            <div className="muted small mono">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        {u.role === "platform_admin" ? (
                          <span className="badge badge-platform">platform</span>
                        ) : (
                          <span className="muted small">org user</span>
                        )}
                      </td>
                      <td>
                        {u.is_org_admin ? (
                          <span className="badge badge-live">admin</span>
                        ) : (
                          <span className="subtle">member</span>
                        )}
                      </td>
                      <td className="muted small">
                        {relativeTime(u.last_login_at)}
                      </td>
                      <td className="right">
                        <div
                          className="btn-row"
                          style={{ justifyContent: "flex-end" }}
                        >
                          <button
                            className="btn sm"
                            onClick={() => startEdit(u)}
                          >
                            Edit
                          </button>
                          {!isSelf && (
                            <button
                              className="btn sm danger"
                              onClick={() => remove(u)}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <SlideOut
          title={
            editing === "new"
              ? "Invite new user"
              : `Edit ${form.name || form.email}`
          }
          subtitle={
            editing === "new"
              ? "Create a user account in this workspace. They can sign in immediately with the password you set."
              : "Update this user's profile. Leave password blank to keep the existing one."
          }
          onClose={closePanel}
        >
          <div className="form-field">
            <label>Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              readOnly={editing !== "new"}
              style={editing !== "new" ? { background: "#f6f6f6" } : {}}
              placeholder="teammate@company.com"
            />
          </div>
          <div className="form-field">
            <label>Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Jane Smith"
            />
          </div>
          <div className="form-field">
            <label>
              {editing === "new" ? "Password" : "New password"}
            </label>
            <input
              type="password"
              value={form.password}
              onChange={(e) =>
                setForm({ ...form, password: e.target.value })
              }
              placeholder={
                editing === "new"
                  ? "Minimum 8 characters"
                  : "Leave blank to keep"
              }
            />
          </div>
          {isPlatformAdmin && (
            <div className="form-field">
              <label>Role</label>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
              >
                <option value="org_user">Organization user</option>
                <option value="platform_admin">Platform admin</option>
              </select>
              <div className="help">
                Platform admins can see every organization and bypass org
                scoping.
              </div>
            </div>
          )}
          <div className="form-field">
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={form.is_org_admin}
                onChange={(e) =>
                  setForm({ ...form, is_org_admin: e.target.checked })
                }
              />
              <div>
                <div style={{ fontWeight: 600 }}>Organization admin</div>
                <div className="muted small">
                  Can manage users, tenants, and settings in this org.
                </div>
              </div>
            </label>
          </div>

          <div
            className="btn-row"
            style={{ marginTop: 24, justifyContent: "flex-end" }}
          >
            <button className="btn" onClick={closePanel} disabled={busy}>
              Cancel
            </button>
            <button className="btn primary" onClick={save} disabled={busy}>
              {busy
                ? "Saving..."
                : editing === "new"
                ? "Create user"
                : "Save changes"}
            </button>
          </div>
        </SlideOut>
      )}
    </>
  );
}

// Same slide-out helper used on AdminOrganizations
function SlideOut({ title, subtitle, onClose, children }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="slideout-backdrop" onClick={onClose}>
      <div className="slideout-panel" onClick={(e) => e.stopPropagation()}>
        <div className="slideout-header">
          <div>
            <h2 className="slideout-title">{title}</h2>
            {subtitle && <p className="slideout-subtitle">{subtitle}</p>}
          </div>
          <button
            type="button"
            className="slideout-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="slideout-body">{children}</div>
      </div>
    </div>
  );
}
